/**
 * Pair — 唯一入口页。
 *   - 带 ?code=XXX：自动 handoff 配对（扫桌面 QR 跳进来 / 应用内扫码）
 *   - 不带 code：扫码落地页（主动开相机扫码 + 配对说明）
 *
 * handoff 无需 JWT，server mint 24h mobile JWT，存 localStorage 后进 Space。
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { handoffPair } from '../lib/api'
import { setToken, isAuthenticated } from '../lib/auth'
import { useLang } from '../contexts/LanguageContext'
import QrScanner from '../components/QrScanner'
import InstallPrompt from '../components/InstallPrompt'
import { Spinner, LangToggle } from '../components/ui'
import { IconScan, IconBack, IconCheck, IconAlert } from '../components/icons'

type Phase = 'landing' | 'scanning' | 'pairing' | 'success' | 'error'

export default function Pair() {
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useLang()
  const urlCode = params.get('code') || ''
  const authed = isAuthenticated()

  const [phase, setPhase] = useState<Phase>(urlCode ? 'pairing' : 'landing')
  const [error, setError] = useState<string | null>(null)
  const [spaceTitle, setSpaceTitle] = useState<string | null>(null)

  const runPair = useCallback(
    (code: string) => {
      setPhase('pairing')
      setError(null)
      handoffPair(code)
        .then((res) => {
          setToken(res.token)
          setSpaceTitle(res.space_title)
          setPhase('success')
          setTimeout(() => navigate(`/spaces/${res.dispatch_space_id}`, { replace: true }), 800)
        })
        .catch((e: Error) => {
          setError(e?.message?.slice(0, 200) || t('pair.failed'))
          setPhase('error')
        })
    },
    [navigate, t],
  )

  useEffect(() => {
    if (urlCode) runPair(urlCode)
  }, [urlCode, runPair])

  if (phase === 'scanning') {
    return <QrScanner onResult={runPair} onClose={() => setPhase('landing')} />
  }

  return (
    <div className="flex min-h-dvh flex-col bg-canvas px-6">
      {phase === 'landing' && (
        <div className="mt-safe flex items-center justify-between">
          {authed ? (
            <button
              onClick={() => navigate('/spaces')}
              className="-ml-1.5 flex items-center gap-1 py-3 text-sm text-muted min-h-tap"
            >
              <IconBack className="h-4 w-4" />
              {t('pair.myProjects')}
            </button>
          ) : (
            <span />
          )}
          <LangToggle />
        </div>
      )}

      <div className="flex flex-1 flex-col items-center justify-center py-12">
        {phase === 'landing' && <Landing onScan={() => setPhase('scanning')} />}
        {phase === 'pairing' && (
          <div className="reveal-in flex flex-col items-center gap-4">
            <Spinner className="h-7 w-7 text-accent" />
            <p className="text-sm text-muted">{t('pair.pairing')}</p>
          </div>
        )}
        {phase === 'success' && (
          <div className="reveal flex flex-col items-center gap-4 text-center">
            <Badge tone="success">
              <IconCheck className="h-7 w-7" strokeWidth={2.4} />
            </Badge>
            <div>
              <p className="font-heading text-xl text-primary">{t('pair.success')}</p>
              <p className="mt-1 text-sm text-muted">
                {spaceTitle ? t('pair.enterSpace', { title: spaceTitle }) : t('pair.redirecting')}
              </p>
            </div>
          </div>
        )}
        {phase === 'error' && (
          <div className="reveal flex w-full max-w-sm flex-col items-center gap-4 text-center">
            <Badge tone="error">
              <IconAlert className="h-6 w-6" />
            </Badge>
            <div>
              <p className="font-heading text-xl text-primary">{t('pair.failed')}</p>
              <p className="mt-1 break-words text-sm text-muted">{error}</p>
              <p className="mt-2 text-xs text-dimmed">{t('pair.codeExpiredHint')}</p>
            </div>
            <div className="flex w-full flex-col gap-2">
              <button
                onClick={() => setPhase('scanning')}
                className="w-full rounded-lg bg-accent py-3 text-sm font-medium text-white min-h-tap"
              >
                {t('pair.rescan')}
              </button>
              {authed && (
                <button
                  onClick={() => navigate('/spaces')}
                  className="w-full rounded-lg border border-line py-3 text-sm text-secondary min-h-tap"
                >
                  {t('pair.backToProjects')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {phase === 'landing' && (
        <div className="pb-safe">
          <InstallPrompt />
        </div>
      )}
    </div>
  )
}

function Landing({ onScan }: { onScan: () => void }) {
  const { t } = useLang()
  return (
    <div className="reveal flex w-full max-w-sm flex-col items-center text-center">
      {/* 扫码取景框装饰 */}
      <div className="relative mb-8 flex h-28 w-28 items-center justify-center">
        <FrameCorner className="left-0 top-0 border-l-2 border-t-2" />
        <FrameCorner className="right-0 top-0 border-r-2 border-t-2" />
        <FrameCorner className="bottom-0 left-0 border-b-2 border-l-2" />
        <FrameCorner className="bottom-0 right-0 border-b-2 border-r-2" />
        <IconScan className="h-11 w-11 text-accent" strokeWidth={1.6} />
      </div>

      <h1 className="font-heading text-4xl tracking-tight text-primary">Cortex</h1>
      <p className="mt-2 text-sm text-muted">{t('pair.tagline')}</p>

      <p className="mt-7 text-sm leading-relaxed text-dimmed">{t('pair.landingDesc')}</p>

      <button
        onClick={onScan}
        className="mt-8 flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-3.5 text-base font-medium text-white transition-colors active:bg-accent-hover min-h-tap"
      >
        <IconScan className="h-5 w-5" />
        {t('pair.scanToPair')}
      </button>
    </div>
  )
}

function FrameCorner({ className }: { className: string }) {
  return <div className={`absolute h-7 w-7 rounded-[4px] border-accent/55 ${className}`} />
}

function Badge({ tone, children }: { tone: 'success' | 'error'; children: React.ReactNode }) {
  const ring = tone === 'success' ? 'bg-success/15 text-success' : 'bg-error/15 text-error'
  return <div className={`flex h-16 w-16 items-center justify-center rounded-full ${ring}`}>{children}</div>
}
